import { getDashboardTodayLocalDate } from "./dashboardTime";

export const METRICS_PRESET_OPTIONS = [
  { value: "this_month", label: "This month" },
  { value: "last_month", label: "Last month" },
  { value: "last_3_months", label: "Last 3 months" },
  { value: "last_week", label: "Last week" },
  { value: "today", label: "Today" },
  { value: "yesterday", label: "Yesterday" },
  { value: "last_quarter", label: "Last quarter" },
  { value: "custom", label: "Custom range" },
  { value: "all", label: "All time" },
];

/** VA distributor dashboard stats default to the current month. */
export const DISTRIBUTOR_METRICS_DEFAULT_FILTER = { period: "this_month" };
export const DISTRIBUTOR_METRICS_EMPTY_LABEL = "This month";

export function startOfDay(date) {
  const next = new Date(date);
  next.setHours(0, 0, 0, 0);
  return next;
}

export function formatISODate(date) {
  if (!date) return "";
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

/** Parse YYYY-MM-DD (or Date) as a local calendar day — avoids UTC day-shift. */
export function parseCalendarDate(value) {
  if (value instanceof Date) return value;
  const raw = String(value || "").trim();
  const match = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (match) {
    return new Date(
      Number(match[1]),
      Number(match[2]) - 1,
      Number(match[3]),
      12,
      0,
      0
    );
  }
  return new Date(value);
}

/** Calendar-date labels (YYYY-MM-DD bounds) — not UTC timestamps. */
export function formatRangeLabel(start, end) {
  if (!start) return "Custom range";
  const formatter = new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
  if (!end) return formatter.format(start);
  return `${formatter.format(start)} – ${formatter.format(end)}`;
}

export function getMetricsFilterLabel(filter = {}, { emptyLabel = "This month" } = {}) {
  if (!filter.period || filter.period === "all") {
    return filter.period === "all" ? "All time" : emptyLabel;
  }

  if (filter.period === "custom" && filter.startDate && filter.endDate) {
    return formatRangeLabel(
      parseCalendarDate(filter.startDate),
      parseCalendarDate(filter.endDate)
    );
  }

  const preset = METRICS_PRESET_OPTIONS.find(
    (option) => option.value === filter.period
  );
  return preset?.label ?? emptyLabel;
}

export function getPresetRange(preset) {
  // Presets use Pacific "today" so dashboard ranges match displayed times.
  const today = startOfDay(getDashboardTodayLocalDate());

  switch (preset) {
    case "today":
      return [today, today];
    case "yesterday": {
      const day = new Date(today);
      day.setDate(day.getDate() - 1);
      return [day, day];
    }
    case "last_week": {
      const end = new Date(today);
      const start = new Date(today);
      start.setDate(start.getDate() - 6);
      return [start, end];
    }
    case "this_month": {
      return [new Date(today.getFullYear(), today.getMonth(), 1), today];
    }
    case "last_month": {
      const start = new Date(today.getFullYear(), today.getMonth() - 1, 1);
      const end = new Date(today.getFullYear(), today.getMonth(), 0);
      return [start, end];
    }
    case "last_3_months": {
      const start = new Date(today.getFullYear(), today.getMonth() - 2, 1);
      return [start, today];
    }
    case "last_quarter": {
      const currentQuarter = Math.floor(today.getMonth() / 3);
      if (currentQuarter === 0) {
        return [
          new Date(today.getFullYear() - 1, 9, 1),
          new Date(today.getFullYear() - 1, 11, 31),
        ];
      }
      const startMonth = (currentQuarter - 1) * 3;
      return [
        new Date(today.getFullYear(), startMonth, 1),
        new Date(today.getFullYear(), startMonth + 3, 0),
      ];
    }
    default:
      return [null, null];
  }
}

export function getDraftStateFromFilter(filter = {}) {
  if (!filter.period || filter.period === "all") {
    return { preset: filter.period === "all" ? "all" : null, start: null, end: null };
  }

  if (filter.period === "custom" && filter.startDate && filter.endDate) {
    return {
      preset: "custom",
      start: startOfDay(new Date(filter.startDate)),
      end: startOfDay(new Date(filter.endDate)),
    };
  }

  const preset = filter.period;
  const [start, end] = getPresetRange(preset);
  return { preset, start, end };
}

export function hasValidMetricsFilter(filter = {}) {
  if (!filter.period) return false;
  if (filter.period === "custom") {
    return Boolean(filter.startDate && filter.endDate);
  }
  return true;
}
